import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import CustomField from "components/form/CustomField";
import { handleSubmitForm } from "helpers/apiUtils";
import {
  forgotPasswordSendEmail,
  forgotPasswordSetNewPassword,
  forgotPasswordVerifyEmailOTP,
} from "store/slices/authSlice";
import { decryptText } from "helpers/cryptoUtils";
import { Images } from "assets/images";
import { useTranslation } from "react-i18next";
import {
  forgotPasswordSchema,
  getForgottenResetValidationSchema,
  verifyOTPForgotPasswordSchema,
} from "utils/validations";
import { Eye, EyeOff } from "lucide-react";
import { Icons } from "assets/icons";
import { useSmartBack } from "hooks/useSmartBack";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector((state) => state.Auth);
  const { logo } = Images || {};

  const [forgotPasswordStep, setForgotPasswordStep] =
    useState("FORGET_PASSWORD");
  const [sessionId, setSessionId] = useState(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Read encrypted values
  const savedEmail = decryptText(localStorage.getItem("remember_email") || "");
  const goBack = useSmartBack();
  const { arrowRight } = Icons;

  const handleSubmitEmail = (values) => {
    const payload = {
      email: values.email,
    };
    handleSubmitForm({
      dispatch,
      action: forgotPasswordSendEmail,
      payload,
      onSuccess: (data) => {
        setForgotPasswordStep("VERIFY_OTP");
        setSessionId(data?.sessionId);
      },
    });
  };
  const handleSubmitEmailOTP = (values) => {
    const payload = {
      sessionId,
      otp: values.otp,
    };
    handleSubmitForm({
      dispatch,
      action: forgotPasswordVerifyEmailOTP,
      payload,
      onSuccess: (data) => {
        setForgotPasswordStep("RESET_PASSWORD");
        setSessionId(data?.sessionId);
      },
    });
  };
  const handleSubmitChangePassword = (values) => {
    const payload = {
      sessionId,
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    };
    handleSubmitForm({
      dispatch,
      action: forgotPasswordSetNewPassword,
      payload,
      onSuccess: () => {
        navigate("/login");
      },
    });
  };
  return (
    <div className="page-content login_page d-flex justify-content-center align-items-center">
      <div className="login-box p-4 shadow-sm rounded bg-white">
        {/* Logo */}
        <img
          src={arrowRight}
          width={24}
          height={24}
          alt={t("Icon")}
          onClick={goBack}
        />
        <div className="text-center mb-4">
          <img src={logo} alt={t("Logo")} width={100} height={55} />
        </div>
        {forgotPasswordStep === "FORGET_PASSWORD" && (
          <>
            <h4 className="text-center mb-3">{t("Forgot Password")}</h4>
            <Formik
              initialValues={{
                email: savedEmail || "",
              }}
              validationSchema={forgotPasswordSchema(t)}
              onSubmit={handleSubmitEmail}
            >
              {() => (
                <Form>
                  <div className="form_field">
                    <CustomField
                      name="email"
                      type="email"
                      label={t("Email")}
                      placeholder={t("Enter email")}
                    />
                  </div>

                  <button
                    type="submit"
                    className="main_btn w-100 mt-3"
                    disabled={isLoading}
                  >
                    {isLoading ? t("Sending OTP...") : t("Send OTP")}
                  </button>
                </Form>
              )}
            </Formik>
          </>
        )}
        {forgotPasswordStep === "VERIFY_OTP" && (
          <>
            <h4 className="text-center mb-3">{t("Verify OTP")}</h4>
            <Formik
              initialValues={{
                otp: "",
              }}
              validationSchema={verifyOTPForgotPasswordSchema(t)}
              onSubmit={handleSubmitEmailOTP}
            >
              {() => (
                <Form>
                  <div className="form_field">
                    <CustomField
                      name="otp"
                      type="text"
                      label={t("OTP")}
                      placeholder={t("Enter otp")}
                    />
                  </div>

                  <button
                    type="submit"
                    className="main_btn w-100 mt-3"
                    disabled={isLoading}
                  >
                    {isLoading ? t("Verifying OTP...") : t("Verify OTP")}
                  </button>
                </Form>
              )}
            </Formik>
          </>
        )}
        {forgotPasswordStep === "RESET_PASSWORD" && (
          <>
            <h4 className="text-center mb-3">{t("Verify OTP")}</h4>
            <Formik
              initialValues={{
                email: savedEmail,
              }}
              validationSchema={getForgottenResetValidationSchema(t)}
              onSubmit={handleSubmitChangePassword}
            >
              {() => (
                <Form>
                  <div className="form_field position-relative">
                    <CustomField
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      label={t("Password")}
                      placeholder={t("Enter Password")}
                    />
                    <span
                      className="password_eye"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                  </div>{" "}
                  <div className="form_field position-relative">
                    <CustomField
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      label={t("Confirm Password")}
                      placeholder={t("Enter Confirm Password")}
                    />
                    <span
                      className="password_eye"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </span>
                  </div>
                  <button
                    type="submit"
                    className="main_btn w-100 mt-3"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? t("Changing password...")
                      : t("Change password")}
                  </button>
                </Form>
              )}
            </Formik>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
